// item 1595 (Chad) — Tier 3 SERVER SPEC catalog: the ONE home for the option lists, the rules
// between them, and the vendor-neutral requirement wording printed on the RFQ PDF.
//
// Tier 3 = an enterprise high-availability cluster on custom servers: 3, 5 or 7 IDENTICAL nodes
// (odd, for quorum), out-of-band management REQUIRED, Ubuntu Server LTS always. ONE node spec
// × the node count — no per-role split (Chad: "breaking it up between core and monitoring is a
// Fontainebleau-specific thing. I don't want that").
//
// Range (Chad, 2026-11-03): the CEILING is the strongest box in the Fontainebleau inventory
// (24 cores / 128 MB L3 / 128 GB, HBA, 2 × 480 GB SSD RAID 1, 2 × 25 GbE bonded, redundant PSUs,
// enterprise BMC); the FLOOR is 16 cores and 32 GB memory per node. A new spec opens on the
// ceiling; tier it down. Option envelope from Dell PowerEdge R6615/R6715 spec sheets, but nothing
// here names a brand — the PDF states needs and the manufacturer quotes anything that meets them.
//
// Saved into ServerSpec.SpecJson as { version: 2, node, notes, conversions, rfq } — `rfq` is the
// rendered wording (buildRfq) the backend lays out; `conversions` is stamped server-side when a
// spec becomes a Sales Order and is carried through edits untouched.

export const NODE_COUNTS = [3, 5, 7] as const;

export interface Opt<T> { value: T; label: string }

export type FormFactor = '1U' | '2U';
export type ControllerKind = 'onboard' | 'hba' | 'hwRaid';
export type DiskInterface = 'sataSsd' | 'sasSsd' | 'nvme' | 'sasHdd';
export type Endurance = 'ri' | 'mu';
export type RaidLevel = 'sw1' | 'sw10' | 'raid1' | 'raid10' | 'raid5' | 'raid6';
export type NicSpeed = '10sfp' | '10baseT' | '25sfp28';
export type BmcTier = 'enterprise' | 'datacenter';
export type PsuEfficiency = 'platinum' | 'titanium';
export type Rails = 'sliding' | 'slidingCma' | 'static';
// item 1595 (Chad: "Dell is never going onsite") — parts-only coverage, no onsite tiers
export type Support = '3nbdParts' | '3depot' | '5nbdParts' | '5depot';

// item 1595 (Chad: "add some custom type options where applicable") — these dropdowns also take
// a typed value ("Custom…"); the typed text replaces the catalog wording on the PDF + SO line.
export const CUSTOM = '__custom';
export type CustomField = 'support' | 'rails' | 'nicSpeed' | 'power' | 'tpm';
// item 1598 (Chad: "yes tpm please") — the security chip as its own visible choice
export type Tpm = 'tpm20' | 'tpm20fips';
export const CUSTOM_LABEL: Record<CustomField, string> = {
  support: 'warranty', rails: 'rails', nicSpeed: 'port speed', power: 'power supply', tpm: 'security chip',
};

/** The ONE node spec — every node in the cluster is identical. */
export interface NodeSpec {
  formFactor: FormFactor;
  cpuCores: number;
  cpuCacheMb: number;
  memory: string; // MEMORY_CONFIGS value, e.g. "8x16"
  memSpeed: number;
  controller: ControllerKind;
  diskCount: number;
  diskInterface: DiskInterface;
  diskGb: number;
  endurance: Endurance;
  raid: RaidLevel;
  bootMirrorGb: number; // 0 = boot from the data array
  sed: boolean;
  nicPorts: number;
  nicSpeed: NicSpeed;
  nicBonded: boolean;
  lomPorts: number; // onboard 1 GbE ports wanted (0 = not required)
  bmc: BmcTier;
  psuWatts: number;
  psuEfficiency: PsuEfficiency;
  rails: Rails;
  support: Support;
  tpm: Tpm;
  custom: Partial<Record<CustomField, string>>; // set = that field is a typed custom value
}

export interface SpecConversion { workOrderId: string; number?: string; date?: string }

export interface RfqLine { label: string; value: string }
export interface RfqSection { title: string; lines: RfqLine[] }
export interface RfqRole { name: string; quantity: number; sections: RfqSection[] }
export interface Rfq { cluster: RfqLine[]; roles: RfqRole[]; quoteMustInclude: string[] }

export interface ServerSpecDoc {
  node: NodeSpec;
  notes: string;
  conversions: SpecConversion[];
}

// ---------------------------------------------------------------- option lists

export const FORM_FACTORS: Opt<FormFactor>[] = [
  { value: '1U', label: '1U rack-mount' },
  { value: '2U', label: '2U rack-mount' },
];
export const CORE_FLOOR = 16;
export const CORE_CEILING = 24;
export const CPU_CORES: Opt<number>[] = [16, 20, 24].map((n) => ({ value: n, label: `${n} cores minimum` }));
export const CPU_CACHE_MB: Opt<number>[] = [32, 64, 128].map((n) => ({ value: n, label: `${n} MB L3 minimum` }));

export interface MemoryConfig { value: string; gb: number; dimmGb: number; dimmCount: number; label: string }
// 32 GB per node is the floor, 128 GB the ceiling; every config is a real DIMM population
export const MEMORY_CONFIGS: MemoryConfig[] = [
  {
    value: '2x16', gb: 32, dimmGb: 16, dimmCount: 2, label: '32 GB (2 × 16 GB)',
  },
  {
    value: '4x16', gb: 64, dimmGb: 16, dimmCount: 4, label: '64 GB (4 × 16 GB)',
  },
  {
    value: '2x32', gb: 64, dimmGb: 32, dimmCount: 2, label: '64 GB (2 × 32 GB)',
  },
  {
    value: '8x16', gb: 128, dimmGb: 16, dimmCount: 8, label: '128 GB (8 × 16 GB) — balanced, recommended',
  },
  {
    value: '4x32', gb: 128, dimmGb: 32, dimmCount: 4, label: '128 GB (4 × 32 GB) — room to grow',
  },
];
export const MEMORY_OPTS: Opt<string>[] = MEMORY_CONFIGS.map((m) => ({ value: m.value, label: m.label }));
export const memoryConfig = (v: string): MemoryConfig => MEMORY_CONFIGS.find((m) => m.value === v) ?? MEMORY_CONFIGS[3];

export const MEM_SPEEDS: Opt<number>[] = [
  { value: 3200, label: 'DDR4-3200 or faster' },
  { value: 4800, label: 'DDR5-4800 or faster' },
  { value: 5600, label: 'DDR5-5600 or faster' },
];
export const CONTROLLERS: Opt<ControllerKind>[] = [
  { value: 'onboard', label: 'Onboard SATA (AHCI)' },
  { value: 'hba', label: 'HBA, pass-through (for software RAID)' },
  { value: 'hwRaid', label: 'Hardware RAID, flash-backed cache' },
];
export const DISK_COUNTS: Opt<number>[] = [2, 4, 6, 8].map((n) => ({ value: n, label: `${n} drives` }));
export const DISK_INTERFACES: Opt<DiskInterface>[] = [
  { value: 'sataSsd', label: 'SATA SSD' },
  { value: 'sasSsd', label: 'SAS SSD' },
  { value: 'nvme', label: 'NVMe SSD (U.2/U.3/E3.S)' },
  { value: 'sasHdd', label: 'SAS HDD' },
];
// enterprise drive sizes — SSDs ship 240/480/960 GB…, not consumer 256/512 GB / 1 TB
export const SSD_GB = [240, 480, 960, 1920, 3840];
export const HDD_GB = [1200, 2400, 4000, 8000];
export const ENDURANCE: Opt<Endurance>[] = [
  { value: 'ri', label: 'Read-intensive (≥ 1 DWPD)' },
  { value: 'mu', label: 'Mixed-use (≥ 3 DWPD)' },
];
export const RAID_LEVELS: Opt<RaidLevel>[] = [
  { value: 'sw1', label: 'Software RAID 1 (mdraid)' },
  { value: 'sw10', label: 'Software RAID 10 (mdraid)' },
  { value: 'raid1', label: 'Hardware RAID 1' },
  { value: 'raid10', label: 'Hardware RAID 10' },
  { value: 'raid5', label: 'Hardware RAID 5' },
  { value: 'raid6', label: 'Hardware RAID 6' },
];
export const BOOT_MIRROR_GB: Opt<number>[] = [
  { value: 0, label: 'None — boot from the data array' },
  { value: 240, label: 'Mirrored M.2 NVMe, 240 GB' },
  { value: 480, label: 'Mirrored M.2 NVMe, 480 GB' },
];
export const NIC_PORTS: Opt<number>[] = [2, 4].map((n) => ({ value: n, label: `${n} ports` }));
export const NIC_SPEEDS: Opt<NicSpeed>[] = [
  { value: '10sfp', label: '10 GbE SFP+' },
  { value: '10baseT', label: '10GBASE-T (RJ45)' },
  { value: '25sfp28', label: '25 GbE SFP28' },
];
export const LOM_PORTS: Opt<number>[] = [
  { value: 0, label: 'Not required' },
  { value: 2, label: '2 × 1 GbE' },
];
export const BMC_TIERS: Opt<BmcTier>[] = [
  { value: 'enterprise', label: 'Enterprise (remote console, virtual media, Redfish)' },
  { value: 'datacenter', label: 'Datacenter (enterprise + telemetry streaming)' },
];
export const PSU_WATTS: Opt<number>[] = [550, 700, 800, 1100].map((n) => ({ value: n, label: `${n} W minimum` }));
export const PSU_EFFICIENCY: Opt<PsuEfficiency>[] = [
  { value: 'platinum', label: '80 PLUS Platinum or better' },
  { value: 'titanium', label: '80 PLUS Titanium' },
];
export const RAILS: Opt<Rails>[] = [
  { value: 'slidingCma', label: 'Sliding rails + cable management arm' },
  { value: 'sliding', label: 'Sliding rails' },
  { value: 'static', label: 'Static rails' },
];
export const TPM_OPTS: Opt<Tpm>[] = [
  { value: 'tpm20', label: 'TPM 2.0 required' },
  { value: 'tpm20fips', label: 'TPM 2.0, FIPS 140-certified' },
];
const TPM_TEXT: Record<Tpm, string> = {
  tpm20: 'TPM 2.0 (discrete or firmware) required',
  tpm20fips: 'TPM 2.0, FIPS 140-certified, required',
};

export const SUPPORT: Opt<Support>[] = [
  { value: '3nbdParts', label: '3-year parts, next-business-day ship (we install)' },
  { value: '3depot', label: '3-year parts, return-to-depot repair' },
  { value: '5nbdParts', label: '5-year parts, next-business-day ship (we install)' },
  { value: '5depot', label: '5-year parts, return-to-depot repair' },
];

const label = <T>(opts: Opt<T>[], v: T): string => opts.find((o) => o.value === v)?.label ?? String(v);

export const isHdd = (i: DiskInterface) => i === 'sasHdd';
export const diskSizes = (i: DiskInterface) => (isHdd(i) ? HDD_GB : SSD_GB);

export const formatGb = (gb: number): string => {
  if (gb < 1000) return `${gb} GB`;
  const tb = Math.round((gb / 1000) * 100) / 100;
  return `${tb} TB`;
};

// ---------------------------------------------------------------- the ceiling

/** The ceiling: the strongest Fontainebleau node — tier down from here. */
export const CEILING_NODE: NodeSpec = {
  formFactor: '1U',
  cpuCores: 24,
  cpuCacheMb: 128,
  memory: '8x16',
  memSpeed: 3200,
  controller: 'hba',
  diskCount: 2,
  diskInterface: 'sataSsd',
  diskGb: 480,
  endurance: 'ri',
  raid: 'sw1',
  bootMirrorGb: 0,
  sed: false,
  nicPorts: 2,
  nicSpeed: '25sfp28',
  nicBonded: true,
  lomPorts: 0,
  bmc: 'enterprise',
  psuWatts: 550,
  psuEfficiency: 'platinum',
  rails: 'slidingCma',
  support: '3nbdParts',
  tpm: 'tpm20',
  custom: {},
};

export const ceilingSpec = (): ServerSpecDoc => ({ node: { ...CEILING_NODE, custom: {} }, notes: '', conversions: [] });

export const isCustom = (n: NodeSpec, f: CustomField) => n.custom?.[f] !== undefined;
const customText = (n: NodeSpec, f: CustomField): string | null => {
  const t = n.custom?.[f];
  return t !== undefined && t.trim() ? t.trim() : null;
};

// ---------------------------------------------------------------- rules

export const totalMemoryGb = (n: Pick<NodeSpec, 'memory'>) => memoryConfig(n.memory).gb;

/** Usable capacity of the data array, or null when the drive count can't build that level. */
export const usableGb = (raid: RaidLevel, count: number, sizeGb: number): number | null => {
  switch (raid) {
    case 'sw1':
    case 'raid1':
      return count === 2 ? sizeGb : null;
    case 'sw10':
    case 'raid10':
      return count >= 4 && count % 2 === 0 ? (count / 2) * sizeGb : null;
    case 'raid5':
      return count >= 3 ? (count - 1) * sizeGb : null;
    case 'raid6':
      return count >= 4 ? (count - 2) * sizeGb : null;
    default:
      return null;
  }
};

export const isHardwareRaid = (raid: RaidLevel) => raid === 'raid1' || raid === 'raid10' || raid === 'raid5' || raid === 'raid6';

export interface SpecIssues { errors: string[]; warnings: string[] }

const RAID_NEEDS: Record<RaidLevel, string> = {
  sw1: 'exactly 2 drives', raid1: 'exactly 2 drives', sw10: 'an even count of 4 or more', raid10: 'an even count of 4 or more', raid5: '3 or more drives', raid6: '4 or more drives',
};

export const nodeIssues = (n: NodeSpec): SpecIssues => {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (n.cpuCores < CORE_FLOOR || n.cpuCores > CORE_CEILING) errors.push(`Cores must be ${CORE_FLOOR}–${CORE_CEILING}.`);
  if (!MEMORY_CONFIGS.some((m) => m.value === n.memory)) errors.push('Pick a memory configuration.');
  if (usableGb(n.raid, n.diskCount, n.diskGb) === null) {
    errors.push(`${label(RAID_LEVELS, n.raid)} needs ${RAID_NEEDS[n.raid]} (you have ${n.diskCount}).`);
  }
  if (isHardwareRaid(n.raid) && n.controller !== 'hwRaid') {
    errors.push(`${label(RAID_LEVELS, n.raid)} needs the hardware RAID controller.`);
  }
  if (n.controller === 'onboard' && (n.diskInterface === 'sasSsd' || n.diskInterface === 'sasHdd')) {
    errors.push('Onboard SATA can\'t run SAS drives — pick an HBA or RAID controller.');
  }
  if (!diskSizes(n.diskInterface).includes(n.diskGb)) {
    errors.push(`Pick a drive size offered for ${label(DISK_INTERFACES, n.diskInterface)}.`);
  }
  if (n.controller === 'hwRaid' && !isHardwareRaid(n.raid)) {
    warnings.push('A hardware RAID controller running software RAID is paying for a card you don\'t use — an HBA is enough.');
  }
  (Object.keys(CUSTOM_LABEL) as CustomField[]).forEach((f) => {
    if (isCustom(n, f) && !customText(n, f)) errors.push(`Type the custom ${CUSTOM_LABEL[f]}, or pick one of the options.`);
  });
  if (memoryConfig(n.memory).dimmCount < 8) {
    warnings.push('Fewer than 8 DIMMs leaves memory channels empty — fine for the floor, but 8 smaller DIMMs are faster than 4 larger ones.');
  }
  return { errors, warnings };
};

export const specIssues = (name: string, nodeCount: number, doc: ServerSpecDoc): SpecIssues => {
  const i = nodeIssues(doc.node);
  const errors = [...i.errors];
  if (!name.trim()) errors.unshift('Give the spec a name.');
  if (!(NODE_COUNTS as readonly number[]).includes(nodeCount)) errors.unshift('A Tier 3 cluster is 3, 5 or 7 nodes.');
  return { errors, warnings: i.warnings };
};

// ---------------------------------------------------------------- vendor-neutral wording

const SUPPORT_TEXT: Record<Support, string> = {
  '3nbdParts': '3 years, parts only — replacement parts shipped next business day (advance exchange); we install',
  '3depot': '3 years, parts only — return-to-depot repair',
  '5nbdParts': '5 years, parts only — replacement parts shipped next business day (advance exchange); we install',
  '5depot': '5 years, parts only — return-to-depot repair',
};

const CONTROLLER_TEXT: Record<ControllerKind, string> = {
  onboard: 'Onboard SATA (AHCI) — no add-in storage controller required',
  hba: 'SAS/SATA host bus adapter in pass-through (IT/JBOD) mode — the OS runs the RAID',
  hwRaid: 'Hardware RAID controller with at least 4 GB flash-backed write cache',
};

const NIC_TEXT: Record<NicSpeed, string> = {
  '10sfp': '10 GbE SFP+',
  '10baseT': '10GBASE-T (RJ45)',
  '25sfp28': '25 GbE SFP28',
};

const RAILS_TEXT: Record<Rails, string> = {
  slidingCma: 'Sliding ready-rails with cable management arm',
  sliding: 'Sliding ready-rails',
  static: 'Static rails',
};

const RAID_NAME: Record<RaidLevel, string> = {
  sw1: 'RAID 1', raid1: 'RAID 1', sw10: 'RAID 10', raid10: 'RAID 10', raid5: 'RAID 5', raid6: 'RAID 6',
};

const raidText = (n: NodeSpec): string => {
  const usable = usableGb(n.raid, n.diskCount, n.diskGb);
  const sw = n.raid === 'sw1' || n.raid === 'sw10';
  return `${RAID_NAME[n.raid]} (${sw ? 'Linux software RAID — mdraid' : 'hardware'})${usable ? `, ${formatGb(usable)} usable minimum` : ''}`;
};

export const nodeSections = (n: NodeSpec): RfqSection[] => {
  const mem = memoryConfig(n.memory);
  const ssd = !isHdd(n.diskInterface);
  const drives = `${n.diskCount} × ${formatGb(n.diskGb)} minimum each, enterprise ${label(DISK_INTERFACES, n.diskInterface)}`
    + `${ssd ? `, ${n.endurance === 'mu' ? 'mixed-use (≥ 3 DWPD)' : 'read-intensive (≥ 1 DWPD)'}` : ''}, hot-swap`;
  return [
    {
      title: 'Platform',
      lines: [
        { label: 'Form factor', value: `${n.formFactor} rack-mount, single-socket enterprise server` },
        { label: 'Rails', value: customText(n, 'rails') ?? RAILS_TEXT[n.rails] },
      ],
    },
    {
      title: 'Processor',
      lines: [
        { label: 'Architecture', value: 'x86-64 server-class, 1 socket, current or prior generation' },
        { label: 'Physical cores', value: `${n.cpuCores} minimum` },
        { label: 'L3 cache', value: `${n.cpuCacheMb} MB minimum` },
        { label: 'Features', value: 'Hardware virtualization and memory encryption (SME/SEV or TME class)' },
      ],
    },
    {
      title: 'Memory',
      lines: [
        { label: 'Capacity', value: `${formatGb(mem.gb)} minimum (${mem.dimmCount} × ${mem.dimmGb} GB)` },
        { label: 'Type', value: `ECC registered (RDIMM), ${label(MEM_SPEEDS, n.memSpeed)}` },
        { label: 'Population', value: 'Balanced across memory channels per the platform guidance; free slots for growth preferred' },
      ],
    },
    {
      title: 'Storage',
      lines: [
        { label: 'Controller', value: CONTROLLER_TEXT[n.controller] },
        { label: 'Data drives', value: drives },
        { label: 'RAID', value: raidText(n) },
        { label: 'Boot', value: n.bootMirrorGb > 0 ? `Separate mirrored pair of M.2 NVMe boot devices, ${formatGb(n.bootMirrorGb)} minimum, hardware RAID 1` : 'Boots from the data array — no separate boot device' },
        ...(n.sed ? [{ label: 'Encryption', value: 'Self-encrypting drives (SED) with local key management' }] : []),
      ],
    },
    {
      title: 'Network',
      lines: [
        { label: 'Data ports', value: `${n.nicPorts} × ${customText(n, 'nicSpeed') ?? NIC_TEXT[n.nicSpeed]}${n.nicBonded ? ', bonded (LACP 802.3ad)' : ''}` },
        { label: 'Driver / firmware', value: 'Chipset supported by the in-box Ubuntu Server LTS kernel driver, shipped on current firmware; must hold link under the current LTS kernel' },
        { label: 'Onboard 1 GbE', value: n.lomPorts > 0 ? `${n.lomPorts} × 1 GbE` : 'Not required' },
      ],
    },
    {
      title: 'Management',
      lines: [
        { label: 'Out-of-band (BMC)', value: 'Required — dedicated management Ethernet port' },
        { label: 'License tier', value: n.bmc === 'datacenter' ? 'Datacenter: HTML5 remote console, virtual media, Redfish + IPMI, alerting, telemetry streaming' : 'Enterprise: HTML5 remote console, virtual media, Redfish + IPMI, alerting' },
      ],
    },
    {
      title: 'Power & security',
      lines: [
        { label: 'Power supplies', value: customText(n, 'power') ?? `2 × ${n.psuWatts} W minimum, hot-swap, redundant (1+1), ${label(PSU_EFFICIENCY, n.psuEfficiency)}, 100–240 V AC` },
        { label: 'Security chip', value: customText(n, 'tpm') ?? TPM_TEXT[n.tpm] },
        { label: 'Security', value: 'UEFI Secure Boot, cryptographically signed firmware (silicon root of trust)' },
      ],
    },
    {
      title: 'Support',
      lines: [
        { label: 'Warranty', value: customText(n, 'support') ?? SUPPORT_TEXT[n.support] },
      ],
    },
  ];
};

export const buildRfq = (nodeCount: number, doc: ServerSpecDoc): Rfq => ({
  cluster: [
    { label: 'Cluster size', value: `${nodeCount} identical nodes — an odd count for high-availability quorum; quote all nodes together` },
    { label: 'Consistency', value: 'Every node identical: same platform, CPU model, firmware and BIOS baseline' },
    { label: 'Operating system', value: 'Ubuntu Server LTS (current release) — every component certified or supported with in-box drivers; no OS licence required' },
    { label: 'Management', value: 'Out-of-band management (BMC) with a dedicated port on every node — required' },
    { label: 'Power', value: 'Dual redundant hot-swap power supplies on every node — required' },
    { label: 'Delivery', value: 'Rack-ready with rails; firmware at the current vendor baseline before ship' },
  ],
  roles: [{ name: 'Every node', quantity: nodeCount, sections: nodeSections(doc.node) }],
  quoteMustInclude: [
    `Per-node unit price and extended price for all ${nodeCount} nodes`,
    'The exact platform / model quoted and every component part number',
    'Every line not met, or met by substitution, called out explicitly',
    'Warranty and support terms as quoted',
    'Lead time to ship and the quote validity date',
    'Shipping, taxes and fees as separate lines',
  ],
});

/** What gets saved: the node spec + notes + conversions (carried) + the rendered RFQ wording. */
export const toSpecJson = (nodeCount: number, doc: ServerSpecDoc): string => JSON.stringify({
  version: 2,
  tier: 3,
  node: doc.node,
  notes: doc.notes ?? '',
  conversions: doc.conversions ?? [],
  rfq: buildRfq(nodeCount, doc),
});

/** Forgiving load: missing fields take the ceiling; a v1 (per-role) payload reads its first role. */
export const fromSpecJson = (json: string | null | undefined): ServerSpecDoc => {
  try {
    const raw = JSON.parse(json || '{}');
    const src = raw.node ?? (Array.isArray(raw.roles) ? raw.roles[0] : null) ?? {};
    const node: NodeSpec = { ...CEILING_NODE };
    (Object.keys(CEILING_NODE) as (keyof NodeSpec)[]).forEach((k) => {
      if (src[k] !== undefined && typeof src[k] === typeof CEILING_NODE[k]) (node as any)[k] = src[k];
    });
    if (!MEMORY_CONFIGS.some((m) => m.value === node.memory)) node.memory = CEILING_NODE.memory;
    if (!SUPPORT.some((o) => o.value === node.support)) node.support = CEILING_NODE.support; // pre-11-03 onsite keys
    if (!TPM_OPTS.some((o) => o.value === node.tpm)) node.tpm = CEILING_NODE.tpm;
    node.custom = {};
    if (src.custom && typeof src.custom === 'object') {
      (Object.keys(CUSTOM_LABEL) as CustomField[]).forEach((f) => {
        if (typeof src.custom[f] === 'string') node.custom[f] = src.custom[f];
      });
    }
    return {
      node,
      notes: typeof raw.notes === 'string' ? raw.notes : '',
      conversions: Array.isArray(raw.conversions) ? raw.conversions : [],
    };
  } catch {
    return ceilingSpec();
  }
};

/** One-line summary for the list row. */
export const nodeSummary = (n: NodeSpec): string => {
  const usable = usableGb(n.raid, n.diskCount, n.diskGb);
  return [
    `${n.cpuCores} cores`,
    `${formatGb(totalMemoryGb(n))} RAM`,
    usable ? `${formatGb(usable)} usable (${RAID_NAME[n.raid]})` : `${n.diskCount}× ${formatGb(n.diskGb)}`,
    `${n.nicPorts}× ${customText(n, 'nicSpeed') ?? NIC_TEXT[n.nicSpeed]}`,
  ].join(' · ');
};

/** The Sales Order line description: the full spec the vendor priced, as plain text. */
export const salesOrderDescription = (name: string, nodeCount: number, doc: ServerSpecDoc): string => {
  const lines = [`Tier 3 enterprise server node — ${name.trim()} (${nodeCount}-node HA cluster, Ubuntu Server LTS)`];
  nodeSections(doc.node).forEach((s) => {
    s.lines
      .filter((l) => l.label !== 'Driver / firmware' && l.label !== 'Population' && l.label !== 'Features')
      .forEach((l) => lines.push(`${l.label}: ${l.value}`));
  });
  return lines.join('\n');
};
